import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import { SortOrder } from 'types/interfaces/sort';
import {
  CardAddSource,
  CollectionListFetchRequest,
  CollectionListFetchResponse,
} from '../types';
import { CollectionList } from 'types/interfaces/collectionList';
import { onboardingApi } from '../../onboarding/onboardingApi';
import { meApi } from '../../user/meApi';

export interface AddFromPrintingsRequest {
  cardName: string;
  oracleId: string;
  quantity: number;
  acceptableSets: string[];
  acceptableLanguages: string[];
  acceptedPrintingIds?: number[];  // Add this to pass specific card IDs
  minCondition?: 'NEAR_MINT' | 'SLIGHTLY_PLAYED' | 'MODERATELY_PLAYED' | 'HEAVILY_PLAYED' | 'DAMAGED';
  acceptableConditions?: string[]; // New field for granular condition selection
  acceptFoil: boolean;
  acceptNonFoil: boolean;
  source?: CardAddSource; // telemetry: which surface this add came from
}

export interface AddSimpleRequest {
  cardName: string;
  oracleId: string;
  quantity: number;
  anySet?: boolean; // Optional, defaults to true
  acceptableSets?: string[]; // Only used when anySet is false
  minCondition?: 'NEAR_MINT' | 'SLIGHTLY_PLAYED' | 'MODERATELY_PLAYED' | 'HEAVILY_PLAYED' | 'DAMAGED' | null;
  acceptableLanguages?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  source?: CardAddSource; // telemetry: which surface this add came from
}

export interface WantListEntry {
  id: number;
  userId: number;
  cardName: string;
  oracleId: string;
  quantity: number;
  acceptableSets: string[];
  acceptableSetCodes?: string[]; // NEW: Set codes computed by backend
  acceptedPrintingIds?: number[]; // Specific card IDs that were added
  acceptableLanguages: string[];
  minCondition?: string; // Keep for backward compatibility during migration
  acceptableConditions?: string[]; // Computed from minCondition by backend
  acceptFoil: boolean;
  acceptNonFoil: boolean;
  availableSets?: string[]; // All sets this card was printed in
  availableLanguages?: string[]; // All languages this card exists in
  createdAt: string;
  updatedAt: string;
}

export interface WantListFetchResponse extends CollectionListFetchResponse {
  wantList: CollectionList[];
}

export interface BulkImportRequest {
  cards: Array<{
    cardName: string;
    quantity: number;
    set?: string;
    language?: string;
    condition?: string;
    foil?: boolean;
  }>;
}

export interface BulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    cardName?: string;
    error: string;
  }>;
  importedCards: Array<{
    id: number;
    cardName: string;
    quantity: number;
    oracleId?: string;
  }>;
}

export interface BulkImportTextRequest {
  text: string;
}

export interface BulkImportJSONRequest {
  type: 'text';
  cards: Array<{
    cardName: string;
    quantity: number;
    set?: string;
    language?: string;
    condition?: string;
    foil?: boolean;
  }>;
}

export interface AddSetToWantListRequest {
  setCodes: string[];                    // Max 5 set codes
  quantity: number;                      // 1-4
  acceptableConditions?: string[];       // e.g., ['NEAR_MINT', 'SLIGHTLY_PLAYED']
  acceptableLanguages?: string[];        // e.g., ['en', 'ja'] - lowercase
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  skipExisting?: boolean;                // Default: true
  skipBasicLands?: boolean;              // Default: false
  rarities?: string[];                   // e.g., ['common', 'rare'] - lowercase
}

export interface AddSetToWantListResponse {
  success: boolean;
  totalCardsProcessed: number;
  cardsAdded: number;
  cardsSkipped: number;
  errors: Array<{ setCode: string; cardName?: string; error: string }>;
  sets: Array<{ setCode: string; setName: string; cardsAdded: number; cardsSkipped: number }>;
  message: string;
}

export const wantListApi = createApi({
  reducerPath: 'wantListApi',
  baseQuery: baseQuery,
  tagTypes: ['WantList'],
  endpoints: (builder) => ({
    wantListFetch: builder.infiniteQuery<
      WantListFetchResponse,
      CollectionListFetchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: pageParam.toString(),
          limit: '10'
        });

        // Only add sort if it has a value
        if (queryArg.sort) {
          params.append('sort', queryArg.sort);
        }

        // Only add direction if it has a value (not undefined or empty string)
        // This fixes the issue where empty string parameters were causing backend parsing errors
        if (queryArg.direction) {
          params.append('direction', queryArg.direction);
        }

        return {
          url: `/want-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
      providesTags: ['WantList'],
    }),
    wantListFetchPaginated: builder.query<
      WantListFetchResponse,
      { page: number; sort?: string; direction?: SortOrder; search?: string }
    >({
      query: ({ page, sort, direction, search }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '100' // 100 cards per page
        });

        // Only add sort if it has a value
        if (sort) {
          params.append('sort', sort);
        }

        // Only add direction if it has a value
        if (direction) {
          params.append('direction', direction);
        }

        // Only add search if it has a value
        if (search) {
          params.append('search', search);
        }

        return {
          url: `/want-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['WantList'],
    }),
    addCardToWantList: builder.mutation<void, { oracleId: string }>({
      query: (body) => ({
        url: '/want-list/add-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after adding cards
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
          // Invalidate user cache to reflect potential auto-avatar change
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    cardToWantListQuantityUpdate: builder.mutation<
      void,
      { id: number; change: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/want-list/card/quantity-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
    }),
    addFromPrintings: builder.mutation<WantListEntry, AddFromPrintingsRequest>({
      query: (body) => ({
        url: '/want-list/add-from-printings',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after adding cards
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
          // Invalidate user cache to reflect potential auto-avatar change
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    removeCardFromWantList: builder.mutation<
      void,
      { id: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/want-list/remove-card',
        method: 'DELETE',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
    }),
    getWantListEntry: builder.query<WantListEntry, string>({
      query: (id) => ({
        url: `/want-list/${id}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['WantList'],
    }),
    updateWantListPreferences: builder.mutation<
      WantListEntry,
      {
        id: number;
        acceptableSets?: string[];
        acceptableConditions?: string[]; // New field replacing minCondition
        acceptFoil?: boolean;
        acceptNonFoil?: boolean;
        acceptableLanguages?: string[];
        acceptedPrintingIds?: number[]; // Specific card IDs to include
        sort?: string;
        direction?: SortOrder;
      }
    >({
      query: ({ id, sort, direction, ...body }) => {
        console.log('=== WANT LIST UPDATE API CALL ===');
        console.log('Endpoint:', `/want-list/${id}`);
        console.log('Body being sent to backend:', body);
        return {
          url: `/want-list/${id}`, // Using backend's structure: /want-list/{wantListItemId}
          method: 'PATCH',
          body,
          requiresAuth: true,
        };
      },
      invalidatesTags: ['WantList'],
    }),
    addSimple: builder.mutation<WantListEntry, AddSimpleRequest>({
      query: (body) => ({
        url: '/want-list/add-simple',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after adding cards
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
          // Invalidate user cache to reflect potential auto-avatar change
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    bulkImport: builder.mutation<BulkImportResponse, FormData | BulkImportTextRequest | BulkImportJSONRequest>({
      query: (body) => ({
        url: '/want-list/bulk-import',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 300000, // 5 minute timeout for large imports
      }),
      invalidatesTags: ['WantList'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after bulk import
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
          // Invalidate user cache to reflect potential auto-avatar change
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    deleteAllWantListItems: builder.mutation<{ message: string; deletedCount: number }, void>({
      query: () => ({
        url: '/want-list/delete-all',
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
    }),
    addSetToWantList: builder.mutation<AddSetToWantListResponse, AddSetToWantListRequest>({
      query: (body) => ({
        url: '/want-list/add-set',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding checklist cache after adding set
          dispatch(onboardingApi.util.invalidateTags(['OnboardingChecklist']));
          // Invalidate user cache to reflect potential auto-avatar change
          dispatch(meApi.util.invalidateTags(['User']));
        } catch {
          // Ignore errors - the mutation itself will handle error states
        }
      },
    }),
    exportWantList: builder.query<
      { items: any[]; truncated: boolean },
      { search?: string }
    >({
      query: ({ search }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        const qs = params.toString();
        return {
          url: `/want-list/export${qs ? `?${qs}` : ''}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    batchUpdateWantList: builder.mutation<
      { successCount: number; totalCount: number; failures: { id: number; error: string }[] },
      { ids: number[]; changes: { acceptableConditions?: string[]; acceptFoil?: boolean; acceptNonFoil?: boolean; acceptableLanguages?: string[] } }
    >({
      query: (body) => ({
        url: '/want-list/batch-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['WantList'],
    }),
  }),
});

export const {
  useWantListFetchInfiniteQuery,
  useWantListFetchPaginatedQuery,
  useAddCardToWantListMutation,
  useCardToWantListQuantityUpdateMutation,
  useRemoveCardFromWantListMutation,
  useAddFromPrintingsMutation,
  useGetWantListEntryQuery,
  useUpdateWantListPreferencesMutation,
  useAddSimpleMutation,
  useBulkImportMutation,
  useDeleteAllWantListItemsMutation,
  useAddSetToWantListMutation,
  useBatchUpdateWantListMutation,
  useLazyExportWantListQuery,
} = wantListApi;
